import { api, ApiError } from '../api/client';
import { Product, GroupedProduct } from '../types';

export const getProductByBarcode = async (barcode: string): Promise<Product | null> => {
  try {
    return await api.get<Product>(`/products/${encodeURIComponent(barcode)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
};

export const getAllProducts = async (): Promise<Product[]> => {
  return api.get<Product[]>('/products');
};

export const getProductsByCategory = async (category: string): Promise<Product[]> => {
  const all = await getAllProducts();
  return all.filter((p) => p.category === category);
};

export const getProductsByCategoryCode = async (categoryCode: string): Promise<Product[]> => {
  const all = await getAllProducts();
  return all.filter((p) => (p.categoryCode ?? '') === categoryCode);
};

export const createProduct = async (product: Omit<Product, 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<void> => {
  await api.post('/products', product);
};

export const updateProduct = async (barcode: string, updates: Partial<Product>): Promise<void> => {
  await api.patch(`/products/${encodeURIComponent(barcode)}`, updates);
};

export const deleteProduct = async (barcode: string): Promise<void> => {
  await api.delete(`/products/${encodeURIComponent(barcode)}`);
};

export const getGroupedProductsForStoreManager = async (): Promise<GroupedProduct[]> => {
  return api.get<GroupedProduct[]>('/products/grouped');
};

export const getGroupedProductByName = async (name: string): Promise<GroupedProduct | null> => {
  const grouped = await getGroupedProductsForStoreManager();
  const q = name.trim().toLowerCase();
  if (!q) return null;
  const exact = grouped.find((g) => g.productName.toLowerCase() === q);
  if (exact) return exact;
  return grouped.find((g) => g.productName.toLowerCase().includes(q)) ?? null;
};

export const getProductTableStockTotals = async (): Promise<{ totalIn: number; totalOut: number }> => {
  const grouped = await getGroupedProductsForStoreManager();
  return {
    totalIn: grouped.reduce((sum, g) => sum + g.totalStockIn, 0),
    totalOut: grouped.reduce((sum, g) => sum + g.totalStockOut, 0)
  };
};

export const getLowStockGroupedForStoreManager = async (): Promise<GroupedProduct[]> => {
  const grouped = await getGroupedProductsForStoreManager();
  return grouped.filter((g) => g.balance <= g.lowStockThreshold);
};

export const deleteProductGroup = async (productName: string, categoryCode: string): Promise<void> => {
  const all = await getAllProducts();
  const toDelete = all.filter(
    (p) => (p.categoryCode ?? '') === categoryCode && p.name === productName
  );
  await Promise.all(toDelete.map((p) => deleteProduct(p.barcode)));
};

export const updateProductGroup = async (
  productName: string,
  categoryCode: string,
  updates: Partial<Pick<Product, 'name' | 'category' | 'categoryCode' | 'costPrice' | 'sellingPrice' | 'lowStockThreshold'>>
): Promise<void> => {
  const all = await getAllProducts();
  const group = all.filter(
    (p) => (p.categoryCode ?? '') === categoryCode && p.name === productName
  );
  await Promise.all(group.map((p) => updateProduct(p.barcode, { ...updates })));
};